<template>
  <Navbar></Navbar>
  <div class="new-page">
    <div class="new-t2">
      新闻资讯
    </div>
    <!--
      新闻列表展示区：只展示后端返回的新闻 HTML
      - 优先使用后端在响应 news.html 时注入到 <script id="news-html-template" type="text/html"> 里的实时 HTML（Ctrl+U 可见）
      - 若后端还未注入，则 fallback 到 AJAX 请求 /portal/news/list/html
    -->
    <iframe v-if="newsHtmlContent" :srcdoc="newsHtmlContent" frameborder="0" :style="iframeStyle"
      @load="autoResizeIframe">
    </iframe>
  </div>

  <Footer></Footer>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import Navbar from '@/components/Navbar.vue';
import Footer from '@/components/Footer.vue';
import { getNewsListHtml } from '@/utils/common.js'

// 承载要显示的新闻 HTML 字符串（可能来自后端注入的 script 标签，也可能来自 AJAX 兜底）
const newsHtmlContent = ref('')
const removeImgIpPrefix = (htmlStr) => {
  return htmlStr
    .replace(/http:\/\/180\.184\.29\.211:91/g, '')
    .replace(/http:\/\/180\.184\.29\.211:8991/g, '');
};

const iframeStyle = ref({
  width: '100%',
  height: 'auto',
  minHeight: '600px',
  border: 0,
  visibility: 'hidden' // 默认隐藏
})
// iframe 高度根据内容自适应
const autoResizeIframe = (e) => {
  const iframe = e.target;
  const resize = () => {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      if (doc && doc.body) {
        // 取 body / documentElement 两者的最大滚动高度，避免末位外边距折叠等场景量矮了又露出内部滚动条
        const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight) + 'px';
        // 高度没变就不写，避免反复触发布局抖动
        if (iframe.style.height !== h) {
          iframe.style.height = h;
        }
      }
      iframeStyle.value.visibility = 'visible'
    } catch (err) {
      // 跨域时忽略
    }
  };
  // ResizeObserver 回调里同步改布局会被浏览器判为死循环并报
  // "ResizeObserver loop completed with undelivered notifications"（dev overlay 会全屏弹出），
  // 推迟到下一帧执行打断同步循环
  let pending = false;
  const scheduleResize = () => {
    if (pending) return;
    pending = true;
    iframe.contentWindow.requestAnimationFrame(() => {
      pending = false;
      resize();
    });
  };
  resize();
  try {
    const win = iframe.contentWindow;
    const doc = win.document;
    const hideScrollbar = doc.createElement('style');
    const rootFontSize = getComputedStyle(document.documentElement).fontSize;
    hideScrollbar.textContent =
      `html{font-size:${rootFontSize};}` +
      'html,body{scrollbar-width:none;-ms-overflow-style:none;}' +
      'html::-webkit-scrollbar,body::-webkit-scrollbar{width:0;height:0;}';
    doc.head.appendChild(hideScrollbar);
    // iframe 内链接统一改为顶层窗口打开，并把后端详情地址重写成官网规范地址，
    // 否则点击会在 iframe 内部导航到 dev server 回退的首页壳
    const fixLinks = () => {
      doc.querySelectorAll('a').forEach((a) => {
        const m = (a.getAttribute('href') || '').match(/\/portal\/news\/view\/(\d+)/);
        if (m) {
          a.setAttribute('href', '/news-detail.html?newsId=' + m[1]);
        }
        a.setAttribute('target', '_top');
      });
    };
    fixLinks();
    // 后端返回的 .hero 是静态单条，这里改造成可滑动轮播（只滑前三条），对齐英文页 news-kv 的交互
    initHeroSlider(doc);
    // DOM 改造后文档高度已变化，同步补量一次 iframe 高度，避免异步补量空档期露出 iframe 内部滚动条
    resize();
    // 窗口 load = 所有图片加载完，再量一次
    win.addEventListener('load', resize);
    // 每张封面图加载完成/失败都补量一次，避免图片撑高后出现内部滚动条
    doc.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', resize);
        img.addEventListener('error', resize);
      }
    });
    // 内容尺寸任何变化（字体加载、折行变化等）都同步高度
    if (win.ResizeObserver) {
      new win.ResizeObserver(scheduleResize).observe(doc.body);
    }
  } catch (err) {
    // 跨域时忽略
  }
};


// 把 iframe 内后端返回的静态 .hero 改造成轮播：
// 取卡片列表前三条新闻重建为 3 屏 slide，支持拖拽/触摸滑动 + 底部红线指示点点击切换（对齐英文页 swiper 交互）
const initHeroSlider = (doc) => {
  const hero = doc.querySelector('.hero');
  const cards = Array.from(doc.querySelectorAll('.card-grid > a.card')).slice(0, 3);
  if (!hero || cards.length === 0 || doc.querySelector('.hero-slider')) return;

  // 用卡片数据（封面/标题/日期/链接，链接已被 fixLinks 重写成官网规范地址）生成每屏 slide，内部类名复后端 hero 样式
  const slides = cards.map((card) => {
    const img = card.querySelector('img');
    const title = (card.querySelector('.card-title') || {}).textContent || '';
    const date = (card.querySelector('.card-date') || {}).textContent || '';
    const slide = doc.createElement('div');
    slide.className = 'hero-slide';
    slide.innerHTML =
      '<div class="hero-left">' +
      '<div class="hero-title"></div>' +
      '<div class="hero-date"></div>' +
      '<div class="hero-desc"></div>' +
      '<a class="hero-more" href="' + (card.getAttribute('href') || '#') + '" target="' + (card.getAttribute('target') || '_top') + '">了解更多 〉</a>' +
      '</div>' +
      '<div class="hero-right"></div>';
    slide.querySelector('.hero-title').textContent = title;
    slide.querySelector('.hero-desc').textContent = title;
    slide.querySelector('.hero-date').textContent = date;
    if (img) {
      const slideImg = doc.createElement('img');
      slideImg.src = img.getAttribute('src') || '';
      slideImg.alt = title;
      slide.querySelector('.hero-right').appendChild(slideImg);
    }
    return slide;
  });

  const slider = doc.createElement('div');
  slider.className = 'hero-slider';
  const track = doc.createElement('div');
  track.className = 'hero-track';
  slides.forEach((s) => track.appendChild(s));
  slider.appendChild(track);

  const bullets = doc.createElement('div');
  bullets.className = 'hero-bullets';
  slides.forEach((_, i) => {
    const b = doc.createElement('span');
    b.className = 'hero-bullet';
    b.addEventListener('click', () => go(i));
    bullets.appendChild(b);
  });
  slider.appendChild(bullets);
  hero.parentNode.replaceChild(slider, hero);

  // 轮播样式注入 iframe 内部；桌面/平板/移动三档断点对齐后端 .hero 的原有规则
  const style = doc.createElement('style');
  style.textContent =
    '.hero-slider{position:relative;overflow:hidden;margin-bottom:.82rem;border-radius:.15rem;}\n' +
    '.hero-track{display:flex;transition:transform .6s ease;touch-action:pan-y;user-select:none;-webkit-user-select:none;cursor:grab;}\n' +
    '.hero-slide{flex:0 0 100%;min-width:100%;display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;background:#EFF3F6;border-radius:.15rem;overflow:hidden;}\n' +
    '.hero-slide .hero-left{width:6.5rem;flex-shrink:0;padding:.9rem .62rem;position:relative;}\n' +
    '.hero-slide .hero-right{width:6.14rem;flex-shrink:0;margin:.48rem .41rem .48rem 0;border-radius:.15rem;overflow:hidden;}\n' +
    '.hero-slide .hero-right img{width:100%;height:3.91rem;object-fit:cover;}\n' +
    '.hero-bullets{position:absolute;left:.62rem;bottom:.4rem;display:flex;z-index:10;}\n' +
    '.hero-bullet{width:.82rem;height:1px;background:#BEBEBE;border-radius:0;position:relative;cursor:pointer;}\n' +
    '.hero-bullet::after{content:"";position:absolute;left:0;bottom:0;width:0;height:2px;background:#E70012;}\n' +
    '.hero-bullet.active::after{width:100%;transition:all .6s;}\n' +
    '@media (max-width:992px){.hero-slide{flex-wrap:wrap;}.hero-slide .hero-left{width:100%;}.hero-slide .hero-right{width:calc(100% - .82rem);margin:0 .41rem .48rem;}}\n' +
    '@media (max-width:768px){.hero-slider{padding-bottom:.5rem;}.hero-slide{flex-direction:column-reverse;gap:0;}.hero-slide .hero-left{width:auto;padding:0.2rem .3rem .45rem;}.hero-slide .hero-right{width:100%;margin:0;border-radius:0;}.hero-slide .hero-right img{height:3.2rem;}.hero-bullets{left:.3rem;bottom:.15rem;}.hero-left .hero-more{width: 1.64rem;height: .64rem;        border-radius: .32rem;font-size: .22rem;}.card-body .card-title{font-size: .28rem;line-height: 1.4;height: .8rem;}}\n';
  doc.head.appendChild(style);

  let index = 0;
  const count = slides.length;
  const render = () => {
    track.style.transform = 'translateX(-' + index * 100 + '%)';
    Array.from(bullets.children).forEach((b, i) => {
      b.classList.toggle('active', i <= index);
    });
  };
  const go = (i) => {
    index = Math.max(0, Math.min(count - 1, i));
    track.style.transition = 'transform .6s ease';
    render();
  };

  // pointer 事件统一鼠标/触摸拖拽；touch-action:pan-y 保证竖向滚动页面不受影响
  let dragging = false;
  let moved = false;
  let startX = 0;
  let delta = 0;
  const width = () => slider.clientWidth || 1;
  track.addEventListener('pointerdown', (e) => {
    dragging = true;
    moved = false;
    startX = e.clientX;
    delta = 0;
    track.style.transition = 'none';
  });
  track.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    delta = e.clientX - startX;
    if (Math.abs(delta) > 5) moved = true;
    track.style.transform = 'translateX(' + (-index * width() + delta) + 'px)';
  });
  const finish = () => {
    if (!dragging) return;
    dragging = false;
    go(Math.abs(delta) > width() * 0.15 ? index + (delta < 0 ? 1 : -1) : index);
  };
  track.addEventListener('pointerup', finish);
  track.addEventListener('pointercancel', finish);
  track.addEventListener('pointerleave', finish);
  // 拖拽结束后的那次 click 要拦掉，避免误触进详情页
  track.addEventListener('click', (e) => {
    if (moved) {
      e.preventDefault();
      e.stopPropagation();
      moved = false;
    }
  }, true);
  render();
};

// 从后端注入的 <script type="text/html" id="news-html-template"> 中读取实时 HTML
// 这段 script 位于 #app 外部，Vue 挂载不会覆盖它；Ctrl+U 也能直接看到
const loadNewsHtmlFromDom = () => {
  const tpl = document.getElementById('news-html-template');
  if (tpl && tpl.textContent && tpl.textContent.trim()) {
    // 后端注入时为防提前闭合，把闭合 script 标签转义成了 <\/script>，这里还原后再交给 iframe
    // 注意：.vue 的 script 块内不能出现字面量的闭合 script 标签（会被 SFC 编译器当闭合标签），用拼接绕开
    // newsHtmlContent.value = tpl.textContent.replace(/<\\\/script>/g, '</' + 'script>');
    newsHtmlContent.value = removeImgIpPrefix(
      tpl.textContent.replace(/<\\\/script>/g, '</' + 'script>')
    );
    return true;
  }
  return false;
};


// 兜底：后端还未注入 script 时，走原来的 AJAX 请求
const GetNewsListHtml = async () => {
  try {
    const kvData = await getNewsListHtml({
      pageNum: 1,
      pageSize: 10,
      releaseStatus: 1
    });
    // 后端 /portal/news/list/html 返回的是完整 HTML 字符串
    // 兼容两种格式：纯字符串 或 { htmlContent: '...' }
    // if (typeof kvData === 'string') {
    //   newsHtmlContent.value = kvData;
    // } else if (kvData && typeof kvData.htmlContent === 'string') {
    //   newsHtmlContent.value = kvData.htmlContent;
    // }
    if (typeof kvData === 'string') {
      newsHtmlContent.value = removeImgIpPrefix(kvData);
    } else if (kvData && typeof kvData.htmlContent === 'string') {
      newsHtmlContent.value = removeImgIpPrefix(kvData.htmlContent);
    }
  } catch (error) {
    console.log(error)
  }
};


onMounted(() => {
  // 先用后端注入的实时 HTML；没有再走 AJAX 兜底
  if (!loadNewsHtmlFromDom()) {
    GetNewsListHtml()
  }
})



</script>

<script>
export const pageConfig = {
  title: '新闻资讯 - 蜂巢汽车官网',
  keywords: '蜂巢,蜂巢汽车,发动机,变速箱,电子',
  description: '蜂巢汽车科技集团有限公司，是一家掌握从传统动力到混合动力再到纯电动力（含控制器）全领域动力总成、汽车智能化集成系统等核心技术的零部件企业，具备完整的软、硬件产业链；能为客户提供高效节能环保的汽车动力总成、智能化出行解决方案',
  outputPath: 'news.html'
};

import { createApp } from 'vue';
import App from './news.vue';
import store from '@/store/index';
import "@/assets/styles/global.scss"
import { title } from 'process';

function mountApp() {
  const appNode = document.getElementById('app');
  if (appNode) {
    if (appNode._vueApp) {
      appNode._vueApp.unmount();
      delete appNode._vueApp;
    }
    const app = createApp(App);
    app.use(store);
    appNode._vueApp = app.mount(appNode);
  }
}


// 页面加载完成后挂载
document.addEventListener('DOMContentLoaded', mountApp);

if (module.hot) {
  module.hot.accept('./news.vue', () => {
    mountApp();
  });
}
</script>

<style lang="scss">
.new-page {
  padding: 1.77rem 1.56rem;
  width: calc(100% - 3.12rem);

  .news-kv {
    background: #EFF3F6;
    border-radius: 0.15rem;
    overflow: hidden;
    margin-bottom: 0.82rem;

    .swiper-slide {
      display: flex;
      gap: 1rem;
      align-items: flex-start;
      justify-content: space-between;

      .text-box {
        width: 6.5rem;
        padding: 0.9rem 0.62rem;
        position: relative;

        .title {
          font-weight: bold;
          font-size: 0.25rem;
          color: #000000;
          margin-bottom: 0.21rem;
        }

        .times {
          font-size: 0.19rem;
          font-weight: 300;
          color: #868686;
          margin-bottom: 0.28rem;
        }

        .text {
          font-weight: 400;
          font-size: 0.16rem;
          color: #999;
          line-height: 0.25rem;
        }

      }

      .more-btn {
        position: absolute;
        left: 0.62rem;
        bottom: 1.1rem;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.04rem;
        height: 0.35rem;
        border-radius: 0.26rem;
        border: 1px solid #676767;
        gap: 0.05rem;

        div {
          font-weight: 400;
          font-size: 0.13rem;
          color: #676767;
        }

        img {
          width: 0.05rem;

          &:first-child {
            display: block;
          }

          &:last-child {
            display: none;
          }
        }

        &:hover {
          background: linear-gradient(135deg, #E70012 0%, #EA5433 100%);
          border: unset;

          div {
            color: #fff;
          }

          img {
            &:nth-of-type(1) {
              display: none;
            }

            &:last-child {
              display: block;
            }
          }
        }
      }

      .img-box {
        width: 6.14rem;
        border-radius: 0.15rem;
        overflow: hidden;
        margin: 0.48rem 0.41rem;

        img {
          width: 100%;
          height: 3.91rem;
        }
      }
    }

    .kv_pagination {
      position: absolute;
      left: 0.62rem;
      bottom: 0.4rem;
      z-index: 10;
      width: auto;

      .swiper-pagination-bullet {
        margin: 0;
        width: 0;
        height: 1px;
        background: #BEBEBE;
        opacity: 1;
        border-radius: 0;
        width: 0.82rem;
        position: relative;

        &::after {
          content: "";
          position: absolute;
          left: 0;
          bottom: 0;
          width: 0;
          height: 2px;
          background: #E70012;
        }

        &.active::after {
          width: 100%;
          transition: all 0.6s;
        }
      }
    }
  }

  .new-t1 {
    font-size: 0.24rem;
    color: #E70012;
    font-weight: 800;
  }

  .new-t2 {
    font-size: 0.4rem;
    color: #000000;
    font-weight: 500;
    margin-bottom: 0.54rem;
  }

  .news-list {
    display: flex;
    justify-content: start;
    gap: 0.21rem;
    flex-wrap: wrap;

    .new-img {
      border-radius: 0.15rem;
      overflow: hidden;
      width: 5.22rem;
      height: 2.94rem;
    }

    .new-item {
      width: calc(100% / 3 - 0.15rem);
      border-radius: 0.15rem;
      background: #F6F8FA;
      overflow: hidden;

      .new-img {
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          transition: all 0.6s;
        }
      }

      &:hover {
        .new-img {
          img {
            transform: scale(1.2);
          }
        }
      }
    }

    .new-item-title {
      font-size: 0.18rem;
      color: #000000;
      font-weight: 500;
      margin-top: 0.25rem;
      padding: 0 0.25rem;
      line-height: 0.3rem;
      height: 0.6rem;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-all;
      white-space: normal;
    }

    .new-item-time {
      font-size: 0.16rem;
      color: #868686;
      font-weight: 300;
      margin-top: 0.17rem;
      padding: 0 0.25rem;
      padding-bottom: 0.25rem;
    }

  }
}

@media screen and (max-width: 768px) {
  .new-page {
    padding: 1.77rem 0.45rem;
    width: calc(100% - 0.9rem);

    .news-kv {
      padding-bottom: 1rem;
      background: unset;
      overflow-y: inherit;

      .swiper-wrapper {}

      .swiper-slide {
        flex-direction: column-reverse;
        gap: 0;
        padding: 0.25rem 0;
        background: #EFF3F6;

        .img-box {
          width: calc(100% - 0.5rem);
          margin: 0 0.25rem;
        }

        .text-box {
          width: calc(100% - 0.5rem);
          padding: 0.5rem 0.25rem;
          height: 2rem;
        }

        .more-btn {
          position: unset;
          width: 1.64rem;
          height: 0.64rem;
          border-radius: 0.32rem;
          margin-left: 0.25rem;

          div {
            font-size: 0.22rem;
          }

          img {
            width: 0.08rem;
          }
        }
      }

      .kv-container {
        overflow: inherit;
      }

      .kv_pagination {
        // bottom: -0.5rem;
        position: unset;
      }
    }

    .news-list {
      .new-item {
        width: 100%;

        .new-img {
          width: 100%;
        }
      }

      .new-item-title {
        font-size: 0.28rem;
      }

      .new-item-time {
        font-size: 0.22rem;
      }
    }
  }
}
</style>